import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Loader2, Gift } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SHIPPING = 9.99;
const FIELDS = [
  { name: "full_name", label: "Full name", col: 2 },
  { name: "address1", label: "Address line 1", col: 2 },
  { name: "address2", label: "Address line 2 (optional)", col: 2 },
  { name: "city", label: "City", col: 1 },
  { name: "state", label: "State", col: 1 },
  { name: "zip", label: "ZIP code", col: 1 },
  { name: "phone", label: "Phone", col: 1 },
];

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "", address1: "", address2: "", city: "", state: "", zip: "", phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [rewards, setRewards] = useState([]);
  const [rewardId, setRewardId] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [applyingPromo, setApplyingPromo] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      api.get("/rewards").then(({ data }) => setRewards(data)).catch(() => {});
    }
  }, [user, loading]);

  const reward = rewards.find((r) => r.id === rewardId);
  let discount = 0;
  let ship = SHIPPING;
  if (reward) {
    if (reward.type === "percent_off") discount = subtotal * (reward.value / 100);
    else if (reward.type === "free_shipping") ship = 0;
  }
  let promoDiscount = 0;
  if (promoInfo) {
    if (promoInfo.type === "percent") promoDiscount = subtotal * (promoInfo.value / 100);
    else if (promoInfo.type === "fixed") promoDiscount = Math.min(promoInfo.value, subtotal);
    else if (promoInfo.type === "free_shipping") ship = 0;
  }
  const total = Math.max(0.5, subtotal - discount - promoDiscount + ship);
  const isCompReward = reward && ["free_preroll", "free_gram", "bogo_preroll"].includes(reward.type);

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setApplyingPromo(true);
    try {
      const { data } = await api.post("/promo/validate", { code: promoCode.trim(), subtotal });
      setPromoInfo(data);
      toast.success(`Promo applied: ${data.label}`);
    } catch (err) {
      setPromoInfo(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setApplyingPromo(false);
    }
  };

  const clearPromo = () => {
    setPromoInfo(null);
    setPromoCode("");
  };

  useEffect(() => {
    if (!loading && !user) navigate("/auth?redirect=/checkout");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (items.length === 0) navigate("/cart");
  }, [items, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["full_name", "address1", "city", "state", "zip", "phone"];
    if (required.some((r) => !form[r].trim())) {
      toast.error("Please fill in all required shipping fields");
      return;
    }
    if (!agreed) {
      toast.error("Please confirm you've read the Sales and Refund policies");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity, size: i.size })),
        shipping: form,
        origin_url: window.location.origin,
        reward_id: rewardId || null,
        promo_code: promoInfo ? promoInfo.code : null,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  if (loading || !user) return null;

  return (
    <div className="mx-auto max-w-6xl px-6 py-16">
      <h1 className="mb-10 text-4xl font-black text-[#2b2520]">Checkout</h1>

      {!user.age_verified && (
        <div
          data-testid="verify-warning"
          className="mb-8 flex items-start gap-3 border border-[#f57c00]/40 bg-[#f57c00]/10 p-4 text-sm text-[#b5610a]"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
          <p>
            Your ID is still pending age verification. You can enter shipping
            details, but orders can only be placed once your ID is approved.
          </p>
        </div>
      )}

      <div className="grid gap-12 lg:grid-cols-3">
        <form onSubmit={submit} className="lg:col-span-2" data-testid="shipping-form">
          <h2 className="mb-6 text-lg font-bold text-[#2b2520]">Shipping Address</h2>
          <div className="grid grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.name} className={f.col === 2 ? "col-span-2" : "col-span-2 sm:col-span-1"}>
                <label className="mb-1 block text-xs uppercase tracking-widest text-neutral-500">
                  {f.label}
                </label>
                <input
                  data-testid={`ship-${f.name}`}
                  value={form[f.name]}
                  onChange={(e) => set(f.name, e.target.value)}
                  className="w-full border border-black/15 bg-white px-4 py-3 text-[#2b2520] outline-none transition-colors focus:border-[#c99a63]"
                />
              </div>
            ))}
          </div>
        </form>

        <div className="h-fit border border-black/10 bg-white p-8">
          <h2 className="mb-6 text-lg font-bold text-[#2b2520]">Order Summary</h2>
          <div className="mb-4 space-y-2 text-sm">
            {items.map((i) => (
              <div key={i.key} className="flex justify-between text-neutral-600">
                <span>
                  {i.product.name}
                  {i.sizeLabel ? ` · ${i.sizeLabel}` : ""} × {i.quantity}
                </span>
                <span className="text-[#2b2520]">${(i.unitPrice * i.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2 border-t border-black/10 pt-4 text-sm">
            <div className="flex justify-between text-neutral-600">
              <span>Subtotal</span>
              <span className="text-[#2b2520]">${subtotal.toFixed(2)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-[#2e7d32]" data-testid="reward-discount-line">
                <span>Reward ({reward.label})</span>
                <span>-${discount.toFixed(2)}</span>
              </div>
            )}
            {promoDiscount > 0 && (
              <div className="flex justify-between text-[#2e7d32]" data-testid="promo-discount-line">
                <span>Promo ({promoInfo.code})</span>
                <span>-${promoDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-neutral-600">
              <span>Shipping</span>
              <span className="text-[#2b2520]">{ship === 0 ? "FREE" : `$${ship.toFixed(2)}`}</span>
            </div>
            {isCompReward && (
              <div className="flex justify-between text-[#2e7d32]" data-testid="reward-comp-line">
                <span>{reward.label}</span>
                <span>Added by our team</span>
              </div>
            )}
          </div>
          <div className="my-4 flex justify-between text-lg font-semibold text-[#2b2520]">
            <span>Total</span>
            <span data-testid="checkout-total">${total.toFixed(2)}</span>
          </div>

          <div className="mb-4 border border-black/10 bg-[#f7f3ec] p-3" data-testid="promo-box">
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-[#a06d34]">Promo code</p>
            {promoInfo ? (
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="text-[#2e7d32]">
                  <span className="font-mono font-bold">{promoInfo.code}</span> · {promoInfo.label}
                </span>
                <button
                  type="button"
                  data-testid="promo-remove"
                  onClick={clearPromo}
                  className="text-xs uppercase tracking-widest text-neutral-500 hover:text-[#d32f2f]"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  data-testid="promo-input"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  className="w-full border border-black/15 bg-white px-3 py-2 text-sm text-[#2b2520] outline-none focus:border-[#c99a63]"
                />
                <button
                  type="button"
                  data-testid="promo-apply"
                  onClick={applyPromo}
                  disabled={applyingPromo || !promoCode.trim()}
                  className="whitespace-nowrap border border-[#c99a63] px-4 py-2 text-xs font-semibold uppercase tracking-widest text-[#a06d34] hover:bg-[#c99a63]/10 disabled:opacity-50"
                >
                  {applyingPromo ? "…" : "Apply"}
                </button>
              </div>
            )}
          </div>
          {rewards.length > 0 && (
            <div className="mb-4 border border-[#c99a63]/40 bg-[#c99a63]/10 p-3" data-testid="rewards-picker">
              <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#a06d34]">
                <Gift className="h-4 w-4" /> Apply a reward you won
              </p>
              <div className="space-y-1.5 text-sm">
                <label className="flex cursor-pointer items-center gap-2 text-neutral-600">
                  <input
                    data-testid="reward-none"
                    type="radio"
                    name="reward"
                    checked={rewardId === ""}
                    onChange={() => setRewardId("")}
                    className="accent-[#c99a63]"
                  />
                  No reward
                </label>
                {rewards.map((r) => (
                  <label key={r.id} className="flex cursor-pointer items-center gap-2 text-[#2b2520]">
                    <input
                      data-testid={`reward-${r.id}`}
                      type="radio"
                      name="reward"
                      checked={rewardId === r.id}
                      onChange={() => setRewardId(r.id)}
                      className="accent-[#c99a63]"
                    />
                    {r.label}
                  </label>
                ))}
              </div>
            </div>
          )}
          <label
            data-testid="policy-consent-label"
            className="mb-4 flex cursor-pointer items-start gap-3 border border-black/10 bg-[#f7f3ec] p-3 text-xs text-neutral-600"
          >
            <input
              data-testid="policy-consent"
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 flex-shrink-0 accent-[#c99a63]"
            />
            <span>
              I have read and agree to the{" "}
              <a href="/sales-policy" target="_blank" rel="noreferrer" className="font-medium text-[#a06d34] underline">
                Sales Policy
              </a>{" "}
              and{" "}
              <a href="/refund-policy" target="_blank" rel="noreferrer" className="font-medium text-[#a06d34] underline">
                Refund Policy
              </a>
              . All sales are final.
            </span>
          </label>
          <button
            data-testid="pay-btn"
            onClick={submit}
            disabled={submitting || !agreed}
            className="flex w-full items-center justify-center gap-2 bg-[#c99a63] py-4 text-sm font-semibold uppercase tracking-widest text-[#2b2520] transition-colors hover:bg-[#bb8850] disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Redirecting…
              </>
            ) : (
              "Pay with card"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
